import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useBreakpointsContext } from "@air/provider-media-query";
import { tailwindMerge } from "@air/tailwind-variants";
import noop from "lodash/noop";
import { memo, useMemo, useRef, useState } from "react";
import { useResizeDetector } from "react-resize-detector";
import { AssetGalleryCardSize, useGalleryAssets } from "~/components/Gallery/useGalleryAssets";
import { BoardGalleryCardDesktopSize, BoardGalleryCardMobileSize, useGalleryBoards } from "~/components/Gallery/useGalleryBoards";
import { FileGalleryCardSize, useGalleryFiles } from "~/components/Gallery/useGalleryFiles";
import LoadMoreSpinner from "~/components/LoadMoreSpinner";
import { PublicBoardNullState } from "~/components/PublicBoard/PublicBoardNullState";
import { SelectableGalleryView } from "~/components/SelectableGalleryView/SelectableGalleryView";
import { usePublicPermissionedVisibleCustomFieldsLength } from "~/components/v2/hooks/usePublicPermissionedVisibleCustomFieldsLength";
import { ASSET_CARD_ROW_MT } from "~/constants/assetCard";
import { WKSPC_CLIP_INFO_HEIGHT, WKSPC_CUSTOM_FIELD_DISPLAY_HEIGHT } from "~/constants/WorkspaceSpacing";
import { useCardSizePreference } from "~/hooks/useCardSizePreference";
import { useGroupedGallerySelectedItems } from "~/hooks/useGroupedGallerySelectedItems";
import { useWorkspaceHorizontalPadding } from "~/hooks/useWorkspaceHorizontalPadding";
import { isTitleAndMetadataVisibleSelector } from "~/store/configViews/selectors";
import { usePublicGalleryViewItems } from "~/swr-hooks/gallery/galleryView/usePublicGalleryViewItems";
import { useAirSelector } from "~/utils/ReduxUtils";
export const PublicGalleryView = /*#__PURE__*/ memo((param)=>{
    let { assetItemAdditionalHeight, assetItemHorizontalSpacing, boardItemHorizontalSpacing, boardItemHeight, boardItemWidth, className, containerHorizontalPadding, EmptyState = PublicBoardNullState, fileItemHorizontalSpacing, headerContainerHeight, renderFile, renderAsset, renderBoard, renderHeader, scrollElementRef } = param;
    var _boardsData_items, _boardsData_items1;
    const [_boardsExpanded, setBoardsExpanded] = useState(true);
    const [_assetsExpanded, setAssetsExpanded] = useState(true);
    const { isAboveSmallScreen, isAboveMediumScreen } = useBreakpointsContext();
    const { cardSize } = useCardSizePreference();
    const { data: { boards: boardsData, clips: clipsData, files: filesData }, isLoading, loadNextPage, isEmpty } = usePublicGalleryViewItems();
    const containerRef = useRef(null);
    const { responsiveHorizontalPadding } = useWorkspaceHorizontalPadding();
    const isTitleAndMetadataVisible = useAirSelector(isTitleAndMetadataVisibleSelector);
    const { permissionedVisibleCustomFieldsLength } = usePublicPermissionedVisibleCustomFieldsLength();
    var _clipsData_items, _filesData_items;
    useGroupedGallerySelectedItems((boardsData === null || boardsData === void 0 ? void 0 : boardsData.items) || [], (_clipsData_items = clipsData === null || clipsData === void 0 ? void 0 : clipsData.items) !== null && _clipsData_items !== void 0 ? _clipsData_items : [], (_filesData_items = filesData === null || filesData === void 0 ? void 0 : filesData.items) !== null && _filesData_items !== void 0 ? _filesData_items : []);
    // add extra space to row for wider drop-rearrange area
    const { width = 0, ref: widthRef } = useResizeDetector({
        handleHeight: false,
        refreshMode: "throttle",
        refreshRate: 300
    });
    const boardsSection = useGalleryBoards({
        data: boardsData,
        containerWidth: width,
        renderBoard,
        loadMore: loadNextPage,
        containerHorizontalPadding: containerHorizontalPadding !== null && containerHorizontalPadding !== void 0 ? containerHorizontalPadding : responsiveHorizontalPadding,
        itemHorizontalSpacing: boardItemHorizontalSpacing,
        isSelectable: ()=>true,
        onSectionCollapse: (isCollapsed)=>setBoardsExpanded(!isCollapsed),
        itemHeight: boardItemHeight !== null && boardItemHeight !== void 0 ? boardItemHeight : isAboveSmallScreen ? BoardGalleryCardDesktopSize[cardSize] : BoardGalleryCardMobileSize[cardSize],
        itemWidth: boardItemWidth,
        headerContainerHeight,
        renderHeader
    });
    const fileAdditionalHeight = useMemo(()=>{
        const additionalRows = Math.max(0, permissionedVisibleCustomFieldsLength - 2);
        return additionalRows * WKSPC_CUSTOM_FIELD_DISPLAY_HEIGHT;
    }, [
        permissionedVisibleCustomFieldsLength
    ]);
    const assetAdditionalHeight = useMemo(()=>{
        let height = 0;
        if (isTitleAndMetadataVisible) height += WKSPC_CLIP_INFO_HEIGHT;
        if (permissionedVisibleCustomFieldsLength) height += ASSET_CARD_ROW_MT;
        return height + permissionedVisibleCustomFieldsLength * WKSPC_CUSTOM_FIELD_DISPLAY_HEIGHT;
    }, [
        permissionedVisibleCustomFieldsLength,
        isTitleAndMetadataVisible
    ]);
    const assetsSection = useGalleryAssets({
        renderAsset,
        isFirstSection: !(boardsData === null || boardsData === void 0 ? void 0 : (_boardsData_items = boardsData.items) === null || _boardsData_items === void 0 ? void 0 : _boardsData_items.length),
        data: clipsData,
        containerWidth: width,
        loadMore: loadNextPage,
        containerHorizontalPadding: containerHorizontalPadding !== null && containerHorizontalPadding !== void 0 ? containerHorizontalPadding : responsiveHorizontalPadding,
        itemAdditionalHeight: assetItemAdditionalHeight !== null && assetItemAdditionalHeight !== void 0 ? assetItemAdditionalHeight : assetAdditionalHeight,
        itemHorizontalSpacing: assetItemHorizontalSpacing,
        isSelectable: ()=>true,
        onSectionCollapse: (isCollapsed)=>setAssetsExpanded(!isCollapsed),
        itemHeight: AssetGalleryCardSize[cardSize],
        headerContainerHeight,
        renderHeader
    });
    const filesSection = useGalleryFiles({
        renderFile,
        isFirstSection: !(boardsData === null || boardsData === void 0 ? void 0 : (_boardsData_items1 = boardsData.items) === null || _boardsData_items1 === void 0 ? void 0 : _boardsData_items1.length) && !(clipsData === null || clipsData === void 0 ? void 0 : clipsData.items.length),
        data: filesData,
        containerWidth: width,
        loadMore: loadNextPage,
        containerHorizontalPadding: containerHorizontalPadding !== null && containerHorizontalPadding !== void 0 ? containerHorizontalPadding : responsiveHorizontalPadding,
        isSelectable: ()=>true,
        itemHeight: FileGalleryCardSize[cardSize] + fileAdditionalHeight,
        itemWidth: !isAboveMediumScreen ? 300 : undefined,
        itemHorizontalSpacing: (fileItemHorizontalSpacing !== null && fileItemHorizontalSpacing !== void 0 ? fileItemHorizontalSpacing : !isAboveSmallScreen) ? 0 : 24,
        headerContainerHeight,
        renderHeader
    });
    const sections = [
        boardsSection,
        assetsSection,
        filesSection
    ].filter((section)=>!!section);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                ref: widthRef
            }),
            isLoading ? /*#__PURE__*/ _jsx(LoadMoreSpinner, {
                isVisible: true,
                isLoading: isLoading,
                loadMore: noop
            }) : isEmpty ? /*#__PURE__*/ _jsx(EmptyState, {}) : width ? /*#__PURE__*/ _jsx("div", {
                ref: containerRef,
                className: tailwindMerge("relative mt-6 min-h-full pb-64", className),
                children: /*#__PURE__*/ _jsx(SelectableGalleryView, {
                    scrollElementRef: scrollElementRef,
                    containerRef: containerRef,
                    sections: sections,
                    width: width
                })
            }) : null
        ]
    });
});
PublicGalleryView.displayName = "PublicGalleryView";
